<template>
	<div>
		<base-header class="pb-6 bg-light">
			<b-row align-v="center" class="py-4">
				<b-col cols="7" lg="6">
					<h6 class="h2 d-inline-block mb-0 text-dark">Entitlements Manager</h6>
					&nbsp;
				</b-col>
				<b-col cols="5" lg="6" class="text-right">
					<nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
						<route-bread-crumb></route-bread-crumb>
					</nav>
				</b-col>
			</b-row>

			<div v-if="noAccess">
				<b-card>
					<b-card-header>
						<p class="h2 mb-2">
							<b-icon-exclamation-triangle-fill variant="danger"></b-icon-exclamation-triangle-fill
							>&nbsp;Restricted
						</p>
					</b-card-header>
					<b-card-body>Access is Restricted</b-card-body>
				</b-card>
			</div>

			<div v-else>
				<b-row>
					<b-col xl="12">
						<b-card no-body>
							<b-card-body>
								<div class="primary-table">
									<b-container class="text-xs text-right">
										<b-row>
											<b-col class="text-right">
												<div class="input-group">
													<b-input-group>
														<template #prepend>
															<base-button size="sm" type="light" disabled>
																Filter
															</base-button>
														</template>
														<b-form-input
															id="iccid"
															type="text"
															v-model="iccid"
															size="sm"
															placeholder="ICCID"
															v-on:keyup.enter="filterQuery"
														></b-form-input>
														<b-form-input
															id="msisdn"
															type="text"
															v-model="msisdn"
															size="sm"
															placeholder="MSISDN"
															v-on:keyup.enter="filterQuery"
														></b-form-input>
														<!--
                            <b-form-select
                                id="carrier"
                                size="sm"
                                v-model="carrier_name"
                                :options="carrier_options"
                                @change="filterQuery"
                                title="CARRIER"
                            ></b-form-select>
                            -->
														<template #append>
															<base-button
                                								data-cy="entitlements-search-btn"
																size="sm"
																type="primary"
																title="SEARCH for entitlements"
																v-on:click="filterQuery"
															>
																<i class="fa fa-search"></i>
															</base-button>
															<base-button
                                								v-if="!isReadOnly"
																size="sm"
																type="success"
																:disabled="isEditor"
																title="ADD entitlements"
																v-on:click="addRecord"
																:hidden="isReadOnly"
															>
																<i class="fa fa-plus"></i> New
															</base-button>
															<base-button
																size="sm"
																type="warning"
																title="RELOAD search"
																v-on:click="reloadPage"
															>
																<i class="fa fa-recycle"></i>
															</base-button>
														</template>
													</b-input-group>
												</div>
												<br />
											</b-col>
										</b-row>
									</b-container>
									<div class="grey-sideborder">
										<b-table
											:sort-by.sync="sortBy"
											:sort-desc.sync="sortDesc"
											:items="listItems"
											:fields="fields"
											:busy="isLoading"
											:per-page="0"
											head-row-variant="primary-table"
											class="primary-table w-auto"
											sticky-header="true"
											show-empty
											responsive
											striped
											hover
											outlined
										>
											<template #table-busy>
												<div class="text-center">
													<b-icon icon="three-dots" animation="cylon" font-scale="4" />
												</div>
											</template>

											<template #empty="scope">
												<div v-if="isLoading" class="text-center">
													<b-icon icon="three-dots" animation="cylon" font-scale="4" />
												</div>
												<div v-else class="text-center">
													<b>No data found in search.</b>
												</div>
											</template>

											<template #cell(details)="row">
												<b-button
													size="sm"
													variant="info"
													v-on:click="showModal(row)"
													title="View Details"
												>
													<b-icon icon="binoculars" aria-hidden="true"></b-icon>
												</b-button>
											</template>

											<template #cell(actions)="row">
												<span class="nowrap">
													<base-button
														:hidden="isReadOnly"
														@click="handleClickEvent('activate', row)"
														size="sm"
														type="warning"
													>
														<b-icon
															icon="cloud-upload"
															v-b-tooltip.hover.rightbottom="
																'ACTIVATE entitlements and bypass websheets for this iccid'
															"
														></b-icon>
													</base-button>
													<base-button
														:hidden="isReadOnly"
														@click="handleClickEvent('delete', row)"
														size="sm"
														type="danger"
													>
														<b-icon
															icon="x-circle"
															v-b-tooltip.hover.rightbottom="
																'DELETE entitlements for this iccid'
															"
														></b-icon>
													</base-button>
												</span>
											</template>

											<template #cell(imsi)="row">
												<div v-for="(item, index) in row.value" :key="index">
													{{ item.value }}
												</div>
											</template>

											<template #cell(entitlements)="row">
												<div class="row">
													<div v-for="(item, index) in row.value" :key="index">
														<b-badge style="margin-left: 4px" variant="primary">
															{{ item }}</b-badge
														>
													</div>
												</div>
											</template>

											<template #cell(state)="row">
												<div class="row">
													<div v-for="(item, index) in row.value" :key="index">
														<div class="grey-sideborder">
															<b-badge variant="primary">{{ index }}</b-badge>
															<span v-if="item.properties">
																<b-badge
																	:variant="
																		item.properties.sosAddress === 'not-available'
																			? 'danger'
																			: 'success'
																	"
																	v-b-tooltip.hover
																	:title="item.properties.sosAddress"
																	>sosAddress</b-badge
																>
																<b-badge
																	:variant="
																		item.properties.termsAndConditions ===
																		'not-available'
																			? 'danger'
																			: 'success'
																	"
																	v-b-tooltip.hover
																	:title="item.properties.termsAndConditions"
																	>termsAndConditions</b-badge
																>
															</span>
														</div>
													</div>
												</div>
											</template>
										</b-table>
									</div>
									<br />
									<b-container class="text-xs">
										<b-row>
											<b-col class="text-left">{{ resultsFound }}</b-col>
											<b-col class="text-center">
												<b-pagination
													v-if="total"
													v-model="currentPage"
													:total-rows="total"
													:per-page="length"
												>
												</b-pagination>
											</b-col>
											<b-col class="text-right">{{ resultsUpdated }}</b-col>
										</b-row>
									</b-container>
								</div>
							</b-card-body>
						</b-card>
					</b-col>
				</b-row>
			</div>

			<b-modal id="addModel" v-model="showAddModal" title="Add a New Record" centered>
				<div class="input-group">
					<b-input-group>
						<template #prepend>
							<base-button size="sm" type="primary" disabled style="width: 125px"> ICCID</base-button>
						</template>
						<b-form-input type="text" v-model="add_iccid" :state="state_iccid"></b-form-input>
					</b-input-group>
				</div>
				<br />
				<div class="input-group">
					<b-input-group>
						<template #prepend>
							<base-button size="sm" type="primary" disabled style="width: 125px"> IMSI</base-button>
						</template>
						<b-form-input type="text" v-model="add_imsi" :state="state_imsi"></b-form-input>
					</b-input-group>
				</div>
				&nbsp;
				<br />
				<div class="input-group">
					<b-input-group>
						<template #prepend>
							<base-button size="sm" type="primary" disabled style="width: 125px"> MSISDN</base-button>
						</template>
						<b-form-input type="text" v-model="add_msisdn" :state="state_msisdn"></b-form-input>
					</b-input-group>
				</div>
				&nbsp;
				<br />
				<div class="input-group">
					<b-input-group>
						<template #prepend>
							<base-button type="primary" disabled style="width: 125px"> CARRIER</base-button>
						</template>
						<b-form-select
							id="carrier"
							v-model="add_carrier"
							:options="carrier_options"
							:state="state_carrier"
						></b-form-select>
					</b-input-group>
				</div>
				&nbsp;
				<br />
				<div class="input-group">
					<b-input-group>
						<template #prepend>
							<base-button type="primary" disabled style="width: 125px"> ENTITLEMENTS</base-button>
						</template>
						&nbsp; &nbsp;
						<b-form-checkbox-group
							v-model="add_entitlements"
							:options="entitlement_options"
							:state="state_entitlements"
						></b-form-checkbox-group>
					</b-input-group>
				</div>
				<template #modal-footer>
					<div>
						<base-button type="light" size="sm" class="float-right" @click="showAddModal = false"
							>Cancel
						</base-button>
					</div>
					<div>
						<base-button
							type="primary"
							size="sm"
							class="float-right"
							@click="addRecordSave()"
							v-if="!recordUpdating && state_show_submit"
							>Save
						</base-button>
						<b-button class="btn btn-primary" size="sm" variant="primary" disabled v-if="recordUpdating">
							<b-icon icon="circle-fill" animation="throb" font-scale="1"></b-icon>
							Saving ...
						</b-button>
						<b-button
							class="btn btn-primary"
							size="sm"
							variant="primary"
							disabled
							v-if="!state_show_submit"
						>
							Save
						</b-button>
					</div>
				</template>
			</b-modal>
		</base-header>

		<!-- Info modal -->
		<b-modal title="Details" ok-only centered id="row-details_modal" size="lg">
			<b-tabs content-class="mt-3">
				<b-tab title="FIELDS" active>
					<div class="data-management-table primary-table b-table-sticky-header table-responsive">
						<table class="table b-table text-nowrap table-striped table-hover table-sm">
							<thead class="thead-light white">
								<th>Name</th>
								<th>Value</th>
							</thead>
							<tbody v-for="(key, index) in Object.keys(tbContent)" :key="index">
								<tr class="b-table">
									<td class="bg-light">{{ key }}</td>
									<td>{{ tbContent[key] }}</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-tab>
				<b-tab title="RAW">
					<span class="responsive-table">
						<div v-highlight class="overflow-auto w-auto h-auto">
							<pre><code class="json">{{
                      printJson(modalContent)
                    }}</code></pre>
						</div>
					</span>
				</b-tab>
				<b-tab v-if="infoTabContent.imsi" title="SOS ADDRESS">
					<div class="data-management-table primary-table b-table-sticky-header table-responsive">
						<table class="table b-table text-nowrap table-striped table-hover table-sm">
							<thead class="thead-light white">
								<th>Name</th>
								<th>Value</th>
							</thead>
							<tbody>
								<tr class="b-table">
									<td class="bg-light">ID</td>
									<td>{{ infoTabContent.id }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">Created</td>
									<td>{{ infoTabContent.dateCreated }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">Expires</td>
									<td>{{ infoTabContent.dateExpires }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">MSISDN</td>
									<td>{{ infoTabContent.msisdn }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">Address Line 1</td>
									<td>{{ infoTabContent.address1 }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">Address Line 2</td>
									<td>{{ infoTabContent.address2 }}</td>
								</tr>
								<tr class="b-table">
									<td class="bg-light">City, State, Zipcode</td>
									<td>
										{{
											`${infoTabContent.city}, ${infoTabContent.state} ${infoTabContent.zipcode}`
										}}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</b-tab>
			</b-tabs>
		</b-modal>
	</div>
</template>
<script>
import entitlementsService from "@/common/api-services/entitlements-dashboard-service";
import initService from "@/common/api-services/init-service";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import ToastVue from "@/views/Components/Toast";
import coreutils from "@/common/coreutils";

export default {
	components: {
		RouteBreadCrumb,
	},

	data() {
		return {
			resultsFound: "",
			resultsUpdated: "",
			total: "",
			currentPage: "",
			page: "1",
			sortBy: "ICCID",
			sortDesc: false,
			sortingChanged: false,

			showAddModal: false,
			isReadOnly: false,
			isEditor: false,
			isAdministrator: false,
			isPortalAdmin: false,
			isLoading: false,
			iccid: "",
			msisdn: "",
			carrier_name: "",
			carrier_options: [],
			entitlement_options: ["VoWIFI", "ap2004", "VoLTE", "ap2003"],
			add_iccid: "",
			//state_iccid: false,
			add_imsi: "",
			//state_imsi: false,
			add_msisdn: "",
			//state_msisdn: false,
			add_carrier: "",
			// state_carrier: false,
			add_entitlements: [],
			//state_entitlements: false,
			recordUpdating: false,
			fields: [
				{
					key: "details",
					label: "Details",
					sortable: false,
				},
				{
					key: "actions",
					label: "actions",
					sortable: false,
				},
				{
					key: "iccid",
					label: "iccid",
					sortable: true,
				},
				{
					key: "msisdn",
					label: "MSISDN",
					sortable: true,
				},
				{
					key: "imsi",
					label: "IMSI",
					sortable: true,
				},
				{
					key: "entitlements",
					label: "ENTITLEMENTS",
					sortable: true,
				},
				{
					key: "state",
					label: "STATE",
					sortable: true,
				},
			],
			listItems: [],
			modalContent: {},
			tbContent: {},
			infoTabContent: {},
		};
	},

	computed: {
		noAccess() {
			// Determine access based on menu link configuration
			let userRoles = initService.getUserRoles();
			let link = this.$store.getters["getLinkByLink"](this.$router.currentRoute.path);

			if (!link) {
				return true;
			} else {
				userRoles = userRoles.filter(role => link.roles.includes(role.toUpperCase()));
				return userRoles.length === 0;
			}
		},
		state_iccid() {
			return this.add_iccid.length == 19 || this.add_iccid.length == 20 ? true : false;
		},
		state_imsi() {
			return this.add_imsi.length == 15 ? true : false;
		},
		state_msisdn() {
			return this.add_msisdn.length == 11 ? true : false;
		},
		state_carrier() {
			return this.add_carrier.length > 0 ? true : false;
		},
		state_entitlements() {
			return this.add_entitlements.length > 0 ? true : false;
		},
		state_show_submit() {
			return (
				this.state_iccid &&
				this.state_imsi &&
				this.state_msisdn &&
				this.state_carrier &&
				this.state_entitlements
			);
		},
	},

	async mounted() {
		document.title = "Entitlements - Manager";

		/** NOTE: The following needs to be in data:
     isReadOnly: true,
     isEditor: false,
     isAdministrator: false,
     isPortalAdmin: false,
     */

		//set the roles [isReadOnly, isEditor, isAdministrator, isPortalAdmin]
		this.isReadOnly = initService.getUserRoles().includes("ENT_READ");
		this.isEditor = initService.getUserRoles().includes("ENTITLEMENTS_EDIT");
		this.isAdministrator =
			initService.getUserRoles().includes("PORTAL_ADMIN") ||
			initService.getUserRoles().includes("CARRIER_ADMIN") ||
			initService.getUserRoles().includes("ENTITLEMENTS_ADMIN");
		this.isPortalAdmin = initService.getPortalAdminRole();

		//write to console.
		if (process.env.NODE_ENV === "dev") {
			console.log("PAGE:", document.title);
			console.log("isReadOnly:", this.isReadOnly);
			console.log("isEditor:", this.isEditor);
			console.log("isAdministrator:", this.isAdministrator);
			console.log("isPortalAdmin:", this.isPortalAdmin);
		}

		//get the carrier role
		if (this.isAdministrator || this.isPortalAdmin) {
			this.customer = "";
		} else {
			this.customer = initService.getPortalCarrier();
			// console.log("carrier:" + this.customer);
		}

		this.carrier_name = this.customer;

		//don't do anything unless the user has access
		if (!this.noAccess) {
			await this.getCarriers();
			//await this.loadInventory();
		} else {
			this.$router.push("/restricted").catch(() => {});
		}
	},

	methods: {
		async addRecordSave() {
			this.recordUpdating = true;
			try {
				if (process.env.NODE_ENV === "dev") {
					console.log("add_iccid", this.add_iccid);
					console.log("add_imsi", this.add_imsi);
					console.log("add_msisdn", this.add_msisdn);
					console.log("add_carrier", this.add_carrier);
					console.log("add_entitlements", this.add_entitlements.toString());
				}

				entitlementsService
					.AddEntitlement(
						this.add_iccid,
						this.add_msisdn,
						this.add_imsi,
						this.add_carrier,
						this.add_entitlements.toString()
					)
					.then(res => {
						if (!!res && !JSON.stringify(res).toLowerCase().includes("error")) {
							this.$toast.success({
								component: ToastVue,
								props: {
									description: " Entitlement add successful " + this.add_iccid,
								},
							});
							//set for autosearch
							this.iccid = this.add_iccid;

							//clear data
							this.add_iccid = "";
							this.add_msisdn = "";
							this.add_imsi = "";
							this.add_carrier = "";
							this.add_entitlements = "";
							this.showAddModal = false;

							//reload
							setTimeout(() => {
								this.reloadPage();
							}, 1000);
						} else {
							this.$toast.error({
								component: ToastVue,
								props: {
									description: " Entitlement add failed " + this.add_iccid,
								},
							});
						}
					});
			} catch (e) {
				console.error(e);
			}
		},

		async handleClickEvent(action, row) {
			try {
				switch (action) {
					case "activate":
						await this.activate(row);
						break;
					case "delete":
						await this.delete(row);
						break;
					default:
						console.warn("no action event defined for click event");
						break;
				}
			} catch (e) {
				console.error(e);
			}
		},

		async delete(row) {
			let msg =
				"You are about to delete this entitlement. After this operation the iccid will be removed for this iccid: " +
				row.item["iccid"];
			this.$bvModal
				.msgBoxConfirm(msg, {
					title: "Confirmation",
					size: "sm",
					buttonSize: "sm",
					okVariant: "danger",
					okTitle: "APPLY",
					cancelTitle: "CANCEL",
					footerClass: "p-2",
					hideHeaderClose: false,
					centered: true,
				})
				.then(value => {
					if (value === true) {
						entitlementsService.DeleteEntitlement(row.item["iccid"], row.item["msisdn"]).then(res => {
							if (!!res && !JSON.stringify(res).toLowerCase().includes("error")) {
								this.$toast.success({
									component: ToastVue,
									props: {
										description: " Entitlement delete successful " + row.item["iccid"],
									},
								});
								setTimeout(() => {
									this.reloadPage();
								}, 1000);
							} else {
								this.$toast.error({
									component: ToastVue,
									props: {
										description: " Entitlement delete failed " + row.item["iccid"],
									},
								});
							}
						});
					}
				});
		},

		async activate(row) {
			let msg =
				"You are about to activate this entitlement and bypass websheets for TS49 VOWIFI. After this operation the entitlements will be active for this iccid: " +
				row.item["iccid"];
			this.$bvModal
				.msgBoxConfirm(msg, {
					title: "Confirmation",
					size: "sm",
					buttonSize: "sm",
					okVariant: "success",
					okTitle: "APPLY",
					cancelTitle: "CANCEL",
					footerClass: "p-2",
					hideHeaderClose: false,
					centered: true,
				})
				.then(value => {
					if (value === true) {
						entitlementsService.UpdateEntitlement(row.item["iccid"]).then(res => {
							if (!!res && !JSON.stringify(res).toLowerCase().includes("error")) {
								this.$toast.success({
									component: ToastVue,
									props: {
										description: " Entitlement delete successful " + row.item["iccid"],
									},
								});
								setTimeout(() => {
									this.reloadPage();
								}, 1000);
							} else {
								this.$toast.error({
									component: ToastVue,
									props: {
										description: " Entitlement delete failed " + row.item["iccid"],
									},
								});
							}
						});
					}
				});
		},

		async getCarriers() {
			this.carrier_options = [];
			this.carrier_options.push({value: "", text: "Select Carrier"});
			await entitlementsService.GetCarriers(this.carrier_name).then(res => {
				// console.log(res);
				if (JSON.stringify(res).toLowerCase().includes("null")) {
					return;
				}
				res.forEach(item => {
					this.carrier_options.push({value: item, text: item});
				});
			});
		},

		async filterQuery() {
			this.loadInventory();
		},

		async addRecord() {
			this.showAddModal = true;
		},

		async reloadPage() {
			this.loadInventory();
		},

		async loadInventory() {
			if (this.iccid.length < 19 && this.msisdn < 11) {
				this.$toast.warning("Unable to search for inventory.  Please enter a valid iccid or msisdn.");
				return;
			}
			this.isLoading = true;
			try {
				this.listItems = [];

				entitlementsService
					.GetInventory(this.iccid, this.msisdn, this.carrier_name, this.page)
					.then(res => {
						if (res && res.results) {
							let rows = [];
							res.results.forEach(item => {
								// console.log(item);
								const row = [];
								row.iccid = item.iccid || null;
								row.msisdn = item.msisdn || null;
								row.imsi = item.imsi || null;
								row.entitlements = item.entitlements || null;
								row.state = item.state || null;
								row.data = item;
								if (row.iccid !== null) rows.push(row);
							});
							if (rows.length > 0) {
								this.listItems = rows;
							} else {
								this.listItems = [];
							}
						} else {
							//no data
						}
						this.isLoading = false;
					})
					.catch(e => {
						throw e;
					});
			} catch (err) {
				this.isLoading = false;
				this.listItems = [];
				console.error(err);
			}
		},

		async showModal(content) {
			this.modalContent = content.item.data;
			// console.log("content:", content.item);
			try {
				this.tbContent = {
					iccid: content.item.iccid ? content.item.iccid : null,
					imsi: content.item.imsi[0].value ? content.item.imsi[0].value : null,
					msisdn: content.item.msisdn ? content.item.msisdn : null,
					entitlements: content.item.entitlements ? content.item.entitlements.toString() : null,
					"state.VoWiFi.status": content.item.state["VoWiFi"]
						? content.item.state["VoWiFi"].status
						: "Not Found",
					// "state.VoWiFi.properties.sosAddress": content.item.state['VoWiFi']? content.item.state['VoWiFi'].properties.sosAddress: "Not Found",
					"state.VoLTE.status": content.item.state["VoLTE"]
						? content.item.state["VoLTE"].status
						: "Not Found",
					// "state.VoLTE.properties.sosAddress": content.item.state['VoLTE']? content.item.state['VoLTE'].properties.sosAddress: "Not Found",
					"state.ap2003.status": content.item.state["ap2003"]
						? content.item.state["ap2003"].status
						: "Not Found",
					// "state.ap2003.properties.sosAddress": content.item.state['ap2003']? content.item.state['ap2003'].properties.sosAddress: "Not Found",
					"state.ap2004.status": content.item.state["ap2004"]
						? content.item.state["ap2004"].status
						: "Not Found",
					// "state.ap2004.properties.sosAddress": content.item.state['ap2004']? content.item.state['ap2004'].properties.sosAddress: "Not Found",
					external_subscriber_id: content.item.data["_embedded"] && content.item.data["_embedded"].subscriber.external_subscriber_id
						? content.item.data["_embedded"].subscriber.external_subscriber_id
						: null,
				};
			} catch (e) {
				console.error(e);
				this.tbContent = [];
			}

			await entitlementsService
				.GetAddress(content.item.imsi[0].value, content.item.msisdn, content.item.iccid)
				.then(res => {
					// console.log("GetAddress:", res);
					if (!String(res).toLowerCase().includes("null response")) {
						this.infoTabContent = res;
					} else {
						this.infoTabContent = {};
					}
				});
			this.$bvModal.show("row-details_modal");
		},

		printJson(text) {
			return coreutils.formatJson(text);
		},
	},
};
</script>
